/**
 * Display Name Utilities
 * Professional display name generation and formatting for useSelft Standalone
 */

export interface DisplayNameOptions {
  showAlias?: boolean
  showPeerId?: boolean
  maxLength?: number
}

/**
 * Capitalizes the first letter of each word in a string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export function capitalizeWords(str: string): string {
  return str
    .toLowerCase()
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Generates a professional display name with fallback hierarchy
 * Priority: selfta.displayName > displayName > selfta.alias > alias > peer ID truncated > 'Unknown User'
 * @param peer - Peer data object (can have selfta sub-object)
 * @param options - Display options
 * @returns Professional formatted display name
 */
export function getProfessionalDisplayName(
  peer: {
    displayName?: string
    alias?: string
    peerId?: string
    id?: string
    selfta?: {
      displayName?: string
      alias?: string
      id?: string
    }
  },
  options: DisplayNameOptions = {}
): string {
  const { showAlias = false, maxLength = 50 } = options

  // CRITICAL FIX: Priority 1 - Use selfta.displayName first, then fallback to displayName
  const primaryDisplayName = peer.selfta?.displayName || peer.displayName
  if (primaryDisplayName && primaryDisplayName.trim() && primaryDisplayName.trim() !== '') {
    const displayName = primaryDisplayName.trim()
    // Skip if displayName is just whitespace or generic
    if (displayName !== 'Unknown User' && displayName !== 'Unknown Peer' && displayName !== '') {
      const aliasForDisplay = peer.selfta?.alias || peer.alias
      const formattedName = showAlias && aliasForDisplay && aliasForDisplay.trim()
        ? `${displayName} (@${aliasForDisplay})`
        : displayName

      return truncateText(formattedName, maxLength)
    }
  }

  // CRITICAL FIX: Priority 2 - Format alias professionally (selfta.alias first, then fallback)
  const primaryAlias = peer.selfta?.alias || peer.alias
  if (primaryAlias && primaryAlias.trim() && primaryAlias.trim() !== '') {
    const alias = primaryAlias.trim()

    // Skip if alias is generic fallback
    if (alias !== 'Unknown User' && alias !== 'Unknown Peer' && alias !== '') {
      // If alias looks like a formatted name (contains spaces or underscores), capitalize it
      const professionalName = alias.includes('_') || alias.includes('-') || alias.includes(' ')
        ? capitalizeWords(alias)
        : alias

      const formattedName = showAlias && alias !== professionalName
        ? `${professionalName} (@${alias})`
        : professionalName

      return truncateText(formattedName, maxLength)
    }
  }

  // CRITICAL FIX: Priority 3 - Use truncated peer ID (check selfta.id first)
  const peerId = peer.selfta?.id || peer.peerId || peer.id
  if (peerId && peerId.trim()) {
    // MOBILE FIX: If we have a properly formatted User name from MediaService, use it as-is
    if (peerId.startsWith('User ') && peerId.includes('...')) {
      return peerId // Already formatted by MediaService
    }

    const truncatedId = peerId.length > 12
      ? `${peerId.substring(0, 8)}...${peerId.slice(-4)}`
      : peerId

    return `User ${truncatedId}`
  }

  // Priority 4: Final fallback
  return 'Unknown User'
}

/**
 * Truncates text to specified length with ellipsis
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

/**
 * Gets initials from a display name for avatar purposes
 * @param displayName - Display name to extract initials from
 * @returns 1-2 character initials
 */
export function getInitials(displayName: string): string {
  if (!displayName || !displayName.trim()) return 'U'

  const words = displayName.trim().split(/\s+/)
  if (words.length === 1) {
    return words[0]?.charAt(0).toUpperCase() || 'U'
  }

  return `${words[0]?.charAt(0) || 'U'}${words[words.length - 1]?.charAt(0) || ''}`.toUpperCase()
}

/**
 * Formats caller name specifically for incoming call notifications
 * Provides enhanced display with fallback and proper formatting
 * @param peer - Peer data from connection or call data (can include selfta)
 * @returns Formatted caller name
 */
export function getIncomingCallDisplayName(peer: {
  displayName?: string
  alias?: string
  peerId?: string
  id?: string
  selfta?: {
    displayName?: string
    alias?: string
    id?: string
  }
}): string {
  // MOBILE FIX: Add debugging to understand what data we're receiving
  console.log('🔍 getIncomingCallDisplayName received:', {
    displayName: peer.displayName,
    alias: peer.alias,
    peerId: peer.peerId,
    id: peer.id
  })

  const result = getProfessionalDisplayName(peer, {
    showAlias: false, // Keep it clean for notifications
    maxLength: 30 // Shorter for mobile compatibility
  })

  console.log(`🎯 getIncomingCallDisplayName result: '${result}'`)
  return result
}